/**
 * Gated single-name inline rename: permission/schema gate + the `useInlineEdit`
 * lifecycle wired to `saveNameRename`. Shared by the pipeline card title and
 * the record header title.
 *
 * WHICH PROPERTY: the one the entity type's display name resolves from —
 * `resolveTitlePropertyName` (`ahoy_title` for a task or meeting, `ahoy_name`
 * for a deal or company). A display name composed from several fields (a
 * person's first + last name) or a constant has no single source and is never
 * renamable here.
 *
 * PERMISSION + SCHEMA GATE (fail-closed): `canRename` is only true when that
 * property resolves, its schema is present and user-writable with an editor
 * for its field type, and the viewer holds `ahoy_property_updated` on the
 * entity type. The entity type's registry `readOnly` flag is deliberately NOT
 * part of the gate: it suppresses the generic overview edit toggle, which says
 * nothing about whether the record's name may change — the property's own
 * write access does.
 *
 * Does NOT expose dnd-aware click/keydown handlers (`handleTitleClick` /
 * `handleTitleKeyDown`) — those wrap `startEdit()` with surface-specific
 * containment (e.g. dnd-kit drag suppression on the pipeline card) and stay
 * in the calling component.
 */

import type React from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { EditorKindDescriptor } from '@/lib/crm/inline-edit/editorKinds';
import {
  type InlineEditStatus,
  useInlineEdit,
} from '@/lib/crm/inline-edit/useInlineEdit';
import {
  hasPropertyEditor,
  isPropertyEditable,
} from '@/lib/crm/utils/propertyEditability';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { resolveTitlePropertyName } from './resolveTitleProperty';
import { saveNameRename } from './saveNameRename';

/** Descriptor for the name-rename editor — bare single-line text input,
 *  same shape as `PipelineColumn`'s stage-rename `BARE_TEXT_DESCRIPTOR`. */
export const NAME_DESCRIPTOR: EditorKindDescriptor = {
  kind: 'bare-text',
  commitTrigger: 'enter-or-blur',
  ownsEnterKey: false,
  autoOpenPicker: false,
  defaultOpen: false,
  overlayLayout: 'row',
};

/**
 * Field types whose stored value is a plain string a single-line rename box can
 * write and `.trim()`. The rename editor is a bare text input, so a title
 * resolving from a non-string property (number, boolean, date, select) must
 * stay read-only rather than enable an editor whose save would `.trim()` a
 * non-string draft and throw. `ahoy_title` / `ahoy_name` are `text`.
 */
const STRING_TITLE_FIELD_TYPES = new Set([
  'text',
  'textarea',
  'markdown',
  'email',
  'phone_number',
  'url',
  'domain',
]);

export interface UseInlineNameRenameInput {
  /** The entity being renamed. */
  object: CrmObject;
  /** The entity type's property schemas. An empty map (still loading) fails
   *  closed to read-only. */
  propertySchemas: Readonly<Record<string, PropertyTypeSchema | undefined>>;
}

export interface UseInlineNameRenameResult {
  canRename: boolean;
  isEditing: boolean;
  status: InlineEditStatus;
  draft: unknown;
  errorMessage: string | null;
  announcement: string;
  startEdit: () => void;
  cancelEdit: () => void;
  setDraft: (value: unknown) => void;
  handleInputKeyDown: (e: KeyboardEvent) => void;
  handleBlur: (e: FocusEvent<HTMLDivElement>) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Schema of the property a rename writes; undefined when none resolves. */
  titleSchema: PropertyTypeSchema | undefined;
}

export function useInlineNameRename({
  object,
  propertySchemas,
}: UseInlineNameRenameInput): UseInlineNameRenameResult {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  const entityType = object.entity_type_name;

  const titlePropertyName = resolveTitlePropertyName(propertySchemas);
  const titleSchema = titlePropertyName
    ? propertySchemas[titlePropertyName]
    : undefined;

  const isSchemaEditable =
    !!titleSchema &&
    STRING_TITLE_FIELD_TYPES.has(titleSchema.field_type_name) &&
    isPropertyEditable(titleSchema) &&
    hasPropertyEditor(titleSchema.field_type_name);

  const canRename =
    titlePropertyName !== null &&
    isSchemaEditable &&
    hasPermission(entityType, 'ahoy_property_updated');

  const nameValue = titlePropertyName
    ? (getPropertyValue<string>(object.properties?.[titlePropertyName]) ?? '')
    : '';

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Deliberately NOT stabilizing `object` out of this dep list: the live
  // `getObjectSeq` read inside `saveNameRename` makes a frozen `object` safe
  // to capture, and `useInlineEdit` freezes `onSave` for the duration of an
  // edit (see its `onSaveRef` sync effect) — so the closure that actually
  // runs at commit time was captured when the edit started, not re-created
  // mid-edit.
  const onSaveRename = useCallback(
    async (value: unknown) => {
      const trimmed = (value as string).trim();
      if (!trimmed) {
        throw new Error('Name cannot be empty.');
      }
      if (!orgId || userId == null || titlePropertyName === null) {
        throw new Error('Could not save. Try again.');
      }
      await saveNameRename({
        object,
        orgId,
        userId,
        wireProperties: { [titlePropertyName]: trimmed },
        optimisticProperties: {
          [titlePropertyName]: trimmed,
          ahoy_display_name: trimmed,
        },
      });
    },
    [object, orgId, userId, titlePropertyName]
  );

  const {
    isEditing,
    status,
    draft,
    errorMessage,
    announcement,
    startEdit,
    cancelEdit,
    setDraft,
    handleKeyDown,
    handleBlur: inlineEditHandleBlur,
  } = useInlineEdit({
    descriptor: NAME_DESCRIPTOR,
    initialValue: nameValue,
    onSave: onSaveRename,
    containerRef,
    triggerRef,
  });

  // Blur on empty: cancel (no error-state lock-in); blur on non-empty: commit.
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if ((draft as string).trim().length === 0) {
        cancelEdit();
        return;
      }
      inlineEditHandleBlur(e);
    },
    [draft, cancelEdit, inlineEditHandleBlur]
  );

  return useMemo(
    () => ({
      canRename,
      isEditing,
      status,
      draft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      setDraft,
      handleInputKeyDown: handleKeyDown,
      handleBlur,
      containerRef,
      triggerRef,
      titleSchema,
    }),
    [
      canRename,
      isEditing,
      status,
      draft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      setDraft,
      handleKeyDown,
      handleBlur,
      titleSchema,
    ]
  );
}
